<template>
  <b-modal v-model="modal.active"
           header-bg-variant="dark"
           header-text-variant="white"
           body-class="p-0"
           :title="modal.title"
           no-close-on-esc
           no-close-on-backdrop
           hide-header-close
           centered
           size="sm">

    <div v-if="loadingConfiguration" class="escpos-modal-state">
      <b-icon icon="arrow-clockwise" class="escpos-modal-state-icon"></b-icon>
      <div class="escpos-modal-state-title">Preparando impresión</div>
      <div class="escpos-modal-state-desc">Consultando configuración de impresión.</div>
    </div>

    <!-- Sin endpoint -->
    <div v-else-if="!effectiveEndpoint" class="escpos-modal-state escpos-modal-state--error">
      <b-icon icon="exclamation-circle" class="escpos-modal-state-icon"></b-icon>
      <div class="escpos-modal-state-title">Sin URL configurada</div>
      <div class="escpos-modal-state-desc">
        Configurá la URL del agente externo antes de continuar.
      </div>
    </div>

    <div v-else-if="isExternalPrinterMode" class="escpos-modal-state escpos-modal-state--ready">
      <b-icon icon="cloud-upload" class="escpos-modal-state-icon"></b-icon>
      <div class="escpos-modal-state-title">Agente externo</div>
      <div class="escpos-modal-state-desc">
        Se va a enviar el contenido de la venta en formato JSON al servicio externo.
      </div>
      <div class="escpos-modal-state-endpoint">
        <b-icon icon="link45deg" class="mr-1"></b-icon>{{ effectiveEndpoint }}
      </div>
    </div>

    <div v-else-if="!isAgentUpdated" class="escpos-modal-state escpos-modal-state--error">
      <b-icon icon="exclamation-circle" class="escpos-modal-state-icon"></b-icon>
      <div class="escpos-modal-state-title">Agente local no disponible</div>
      <div class="escpos-modal-state-desc">
        Para imprimir con agente local, instalá o actualizá el servicio desde la configuración de reportes.
      </div>
      <b-button size="sm" variant="dark" :to="{ name: 'ReportsStaffConfiguration' }">
        Ir a configuración
      </b-button>
    </div>

    <!-- Con agente local, listo para imprimir -->
    <div v-else class="escpos-modal-state escpos-modal-state--ready">
      <b-icon icon="printer" class="escpos-modal-state-icon"></b-icon>
      <div class="escpos-modal-state-title">Agente local activo</div>
      <div class="escpos-modal-state-desc">
        Se va a imprimir usando el servicio local instalado en esta computadora.
      </div>
      <div class="escpos-local-summary">
        <b-badge variant="success" pill>Instalado</b-badge>
        <div>Impresora: {{ effectivePrinterName || 'Predeterminada' }}</div>
      </div>
    </div>

    <!-- Selección de formato si hay varios -->
    <div v-if="isLocalPrinterMode && manyFormat && arr.reports.length > 1" class="escpos-modal-formats">
      <div class="escpos-modal-formats-label">Seleccioná un formato:</div>
      <b-list-group flush>
        <b-list-group-item v-for="(item, index) in arr.reports"
                           :key="index"
                           @click="selectFormat(item)"
                           href="#"
                           class="escpos-modal-format-item"
                           :class="{ 'escpos-modal-format-item--selected': selectedReference === item.reference }">
          <b-icon icon="file-earmark-text" class="mr-2"></b-icon>{{ item.name }}
          <b-icon v-if="selectedReference === item.reference" icon="check2" class="ml-auto text-success"></b-icon>
        </b-list-group-item>
      </b-list-group>
    </div>

    <template v-slot:modal-footer>
      <b-button type="button" variant="outline-secondary" size="sm" @click="cancelPrint()">Cancelar</b-button>
      <b-button type="button" variant="dark" size="sm" :disabled="!canPrint" @click="confirmPrint()">
        <b-icon icon="printer" aria-hidden="true"></b-icon> Enviar a impresora
      </b-button>
    </template>

  </b-modal>
</template>

<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Helper from '@/handler/helper'
  import ConfigAPI from '@/config/api'
  import localTicketCache from './localTicketCache'

  export default {
    props: {
      content: {
        type: String,
        default: '',
      },
      printerName: {
        type: String,
        default: '',
      },
      printPayload: {
        type: [Object, Array, String],
        default: null,
      },
      reference: {
        type: String,
        default: '',
      },
      reportsID: {
        type: Number,
        default: 0,
      },
      registerID: {
        type: Number,
        default: 0,
      },
      filters: {
        type: Object,
        default: null,
      },
      manyFormat: {
        type: Boolean,
        default: false,
      },
      payloadField: {
        type: String,
        default: 'content',
      },
      direct: {
        type: Boolean,
        default: false,
      },
    },
    data: () => {
      return {
        modal: {
          active: false,
          title: 'Imprimir ESC/POS',
        },
        arr: {
          reports: [],
        },
        selectedReference: null,
        agentStatus: {
          loading: false,
          connected: false,
          variant: 'secondary',
          label: 'Verificando',
          baseUrl: '',
          installedVersion: '',
          os: '',
          arch: '',
        },
        releaseInfo: {
          version: '',
          releaseBaseUrl: '',
        },
        releaseLinks: {
          windows: {
            installer: '#',
          },
          debian: {
            deb: '#',
          },
        },
        loadingConfiguration: true,
        staffPrinterConfiguration: {
          thermal_printer: '',
          thermal_printer_mode: 'local',
          thermal_printer_external_url: '',
        },
      }
    },
    async mounted() {
      await this.loadStaffPrinterConfiguration()
      if(this.isLocalPrinterMode) {
        await this.loadAgentReleaseInfo()
      }
      this.preparePrint()
    },
    computed: {
      isLocalPrinterMode() {
        return this.staffPrinterConfiguration.thermal_printer_mode != 'external'
      },
      isExternalPrinterMode() {
        return this.staffPrinterConfiguration.thermal_printer_mode == 'external'
      },
      effectiveEndpoint() {
        if(this.isExternalPrinterMode) {
          return this.staffPrinterConfiguration.thermal_printer_external_url || ''
        }

        return 'http://127.0.0.1:8095/print'
      },
      effectivePrinterName() {
        return this.staffPrinterConfiguration.thermal_printer || this.printerName
      },
      platformKey() {
        const os = this.agentStatus.os || this.detectOS()
        if(os == 'windows') return 'windows'
        if(os == 'debian' || os == 'linux') return 'debian'
        return ''
      },
      platformRelease() {
        const platforms = this.releaseInfo.platforms || {}
        if(this.platformKey && platforms[this.platformKey]) {
          return platforms[this.platformKey]
        }

        // Manifiestos anteriores a las versiones por plataforma.
        return {
          version: this.releaseInfo.version || '',
        }
      },
      isAgentUpdated() {
        if (!this.isLocalPrinterMode) return true

        // Fuera de línea no se puede consultar el manifiesto de versiones del
        // backend. El agente local sí puede responder en localhost y debe poder
        // imprimir tickets locales; sólo se exige igualdad de versión cuando el
        // manifiesto remoto fue obtenido.
        if (!this.agentStatus.connected) return false
        if (!this.platformRelease.version) return true

        return this.agentStatus.installedVersion == this.platformRelease.version
      },
      canPrint() {
        return !!this.effectiveEndpoint && (!this.isLocalPrinterMode || this.isAgentUpdated)
      },
    },
    methods: {
      loadStaffPrinterConfiguration() {
        this.loadingConfiguration = true
        var result = serviceAPI.obtenerStaffThermalPrinter()

        return result.then((response) => {
          var data = response.data
          this.staffPrinterConfiguration.thermal_printer = data.thermal_printer || ''
          this.staffPrinterConfiguration.thermal_printer_mode = data.thermal_printer_mode || 'local'
          this.staffPrinterConfiguration.thermal_printer_external_url = data.thermal_printer_external_url || ''
          this.loadingConfiguration = false
        })
        .catch(async error => {
          // Sin conexión: usar la config de impresora cacheada por warmCache()
          // (ver localTicketCache.js) en vez de asumir "local" a ciegas.
          if(!error.response) {
            var cached = await localTicketCache.getPrinterConfig()
            this.staffPrinterConfiguration.thermal_printer = cached.thermal_printer
            this.staffPrinterConfiguration.thermal_printer_mode = cached.thermal_printer_mode
            this.staffPrinterConfiguration.thermal_printer_external_url = cached.thermal_printer_external_url
            this.loadingConfiguration = false
            return
          }

          this.loadingConfiguration = false
          this.$awn.alert(Error.showError(error))
        })
      },
      getAgentBaseUrl() {
        const raw = this.effectiveEndpoint

        try {
          const parsed = new URL(raw, window.location.origin)
          return `${parsed.protocol}//${parsed.host}`
        } catch (error) {
          return 'http://127.0.0.1:8095'
        }
      },
      detectOS() {
        const agent = navigator.userAgent || navigator.platform || ''
        const value = agent.toLowerCase()

        if(value.indexOf('win') !== -1) return 'windows'
        if(value.indexOf('ubuntu') !== -1 || value.indexOf('debian') !== -1) return 'debian'
        if(value.indexOf('linux') !== -1) return 'linux'

        return 'unknown'
      },
      async loadAgentReleaseInfo() {
        this.agentStatus.loading = true
        this.agentStatus.baseUrl = this.getAgentBaseUrl()

        await this.loadLatestReleaseInfo()
        await this.checkLocalAgent()

        this.agentStatus.loading = false
      },
      async loadLatestReleaseInfo() {
        const manifestUrl = `${ConfigAPI.baseURL}public/printer-agent/latest?_=${Date.now()}`

        try {
          const response = await fetch(manifestUrl, { cache: 'no-store' })

          if(!response.ok) {
            throw new Error('manifest not available')
          }

          const data = await response.json()
          this.releaseInfo = data
          this.releaseLinks.windows.installer = this.getPublicDownloadUrl(data.downloads.windows.installer)
          this.releaseLinks.debian.deb = data.downloads.debian ? this.getPublicDownloadUrl(data.downloads.debian.deb) : ''
        } catch (error) {
          this.releaseInfo = {
            version: '',
            releaseBaseUrl: '',
          }
        }
      },
      getPublicDownloadUrl(value) {
        if(!value) {
          return ''
        }

        if(value.indexOf('http://') === 0 || value.indexOf('https://') === 0) {
          return value
        }

        const path = value.replace(/^\/?backend\/public\//, '').replace(/^\//, '')
        return this.getBackendPublicUrl(path)
      },
      getBackendPublicUrl(value) {
        const publicBaseUrl = ConfigAPI.baseURL.replace(/\/api\/?$/, '/')
        return `${publicBaseUrl}${value.replace(/^\//, '')}`
      },
      async checkLocalAgent() {
        const timeout = 1500
        const controller = new AbortController()
        const timer = setTimeout(() => controller.abort(), timeout)

        try {
          const response = await fetch(`${this.agentStatus.baseUrl}/health`, {
            method: 'GET',
            mode: 'cors',
            signal: controller.signal,
          })

          clearTimeout(timer)

          if(!response.ok) {
            throw new Error('unavailable')
          }

          const data = await response.json()
          this.agentStatus.connected = true
          this.agentStatus.installedVersion = data.version || ''
          this.agentStatus.os = data.os || ''
          this.agentStatus.arch = data.arch || ''
          this.agentStatus.variant = 'success'
          this.agentStatus.label = 'Instalado'
        } catch (error) {
          clearTimeout(timer)
          this.agentStatus.connected = false
          this.agentStatus.installedVersion = ''
          this.agentStatus.os = ''
          this.agentStatus.arch = ''
          this.agentStatus.variant = 'warning'
          this.agentStatus.label = 'No detectado'
        }
      },
      preparePrint() {
        // El agente externo recibe el dataset JSON, no una plantilla ESC/POS.
        // Por eso no corresponde pedir al operador que seleccione formato:
        // se usa directamente la referencia por defecto del comprobante.
        if(this.isExternalPrinterMode && !this.content && this.printPayload === null) {
          this.confirmPrint()
          return true
        }

        if(this.direct) {
          if(!this.canPrint) {
            this.$awn.alert(this.printUnavailableMessage())
            this.$emit('error', 'no-endpoint')
            return false
          }

          if(this.manyFormat && !this.content) {
            this.searchFormat()
            return false
          }

          this.confirmPrint()
          return true
        }

        if(this.manyFormat && !this.content) {
          this.searchFormat()
          return false
        }

        this.modal.active = true
      },
      searchFormat() {
        if(!this.reference) {
          return false
        }

        var result = serviceAPI.search({
          reference: this.reference,
        })

        result.then((response) => {
          this.arr.reports = response.data.filter(item => item.type_print == 'escpos' || item.type_print == 'both')

          if(this.arr.reports.length == 1) {
            this.selectedReference = this.arr.reports[0].reference
          }

          if(!this.arr.reports.length) {
            this.$awn.alert('No hay reportes ESC/POS disponibles para esta operación')
            return false
          }

          // En modo directo con un solo formato disponible, enviar sin modal
          if(this.direct && this.arr.reports.length == 1) {
            this.confirmPrint()
            return true
          }

          this.modal.active = true
        })
      },
      selectFormat(item) {
        this.selectedReference = item.reference
      },
      cancelPrint() {
        this.modal.active = false
      },
      confirmPrint() {
        if(!this.canPrint) {
          this.$awn.alert(this.printUnavailableMessage())
          return false
        }

        if(this.isLocalPrinterMode && this.manyFormat && this.arr.reports.length > 1 && !this.selectedReference) {
          this.$awn.alert('Seleccione un formato ESC/POS')
          return false
        }

        if(this.isExternalPrinterMode && this.printPayload !== null) {
          this.sendToPrinter(this.printPayload)
          return true
        }

        if(this.content) {
          this.sendToPrinter(this.content)
          return true
        }

        let reference = this.selectedReference || this.reference

        if(!this.reportsID && !reference) {
          return false
        }

        if(!this.registerID && !this.filters) {
          return false
        }

        if(this.isExternalPrinterMode) {
          this.generateReportsJson(reference)
          return true
        }

        this.generateReports(reference)
      },
      generateReports(reference = null) {
        let loader = this.$loading.show()

        var result = serviceAPI.generateEscpos({
          reportsID: this.reportsID,
          reference: reference,
          regID: this.registerID,
          filters: this.filters,
          staff_id: Helper.getStaffId(),
        })

        result.then((response) => {
          loader.hide()
          var data = response.data
          this.sendToPrinter(this.decodeEscposContent(data))
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },
      decodeEscposContent(data) {
        if(data && data.content_encoding == 'base64' && data.content_base64) {
          return window.atob(data.content_base64)
        }

        return data ? data.content : ''
      },
      generateReportsJson(reference = null) {
        let loader = this.$loading.show()

        var result = serviceAPI.generateEscposJson({
          reportsID: this.reportsID,
          reference: reference,
          regID: this.registerID,
          filters: this.filters,
          staff_id: Helper.getStaffId(),
        })

        result.then((response) => {
          loader.hide()
          var data = response.data
          this.sendToPrinter(data.content)
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },
      sendToPrinter(content) {
        let loader = this.$loading.show()
        let payload = content

        if(this.isExternalPrinterMode) {
          if(content && typeof content === 'object') {
            payload = content
          } else {
            payload = {}
            payload[this.payloadField] = content
          }
        }

        var result = serviceAPI.printEscpos(this.effectiveEndpoint, payload, !this.isExternalPrinterMode, this.effectivePrinterName, this.isLocalPrinterMode)

        result.then((response) => {
          loader.hide()
          this.modal.active = false
          this.$awn.success('Comprobante ESC/POS enviado al servicio de impresión')
          this.$emit('printed', response.data)
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
          this.$emit('error', error)
        })
      },
      printUnavailableMessage() {
        if(this.isLocalPrinterMode && !this.isAgentUpdated) {
          return 'El agente local no está instalado o no está actualizado'
        }

        if(!this.effectiveEndpoint) {
          return 'No se configuró la URL del agente externo'
        }

        return 'No se puede imprimir ESC/POS'
      },
    },
  }
</script>

<style scoped>
.escpos-modal-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 28px 24px 20px;
}
.escpos-modal-state-icon {
  font-size: 2.8rem;
  margin-bottom: 12px;
}
.escpos-modal-state--ready .escpos-modal-state-icon {
  color: #343a40;
}
.escpos-modal-state--error .escpos-modal-state-icon {
  color: #dc3545;
}
.escpos-modal-state-title {
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 8px;
  color: #23282c;
}
.escpos-modal-state--error .escpos-modal-state-title {
  color: #dc3545;
}
.escpos-modal-state-desc {
  font-size: 0.82rem;
  color: #6c757d;
  max-width: 240px;
}
.escpos-modal-state-endpoint {
  font-size: 0.78rem;
  color: #495057;
  background: #f0f2f5;
  border-radius: 6px;
  padding: 5px 10px;
  word-break: break-all;
  max-width: 100%;
}
.escpos-local-summary {
  align-items: center;
  background: #f0f2f5;
  border-radius: 6px;
  color: #495057;
  display: flex;
  flex-direction: column;
  font-size: 0.78rem;
  gap: 6px;
  margin-top: 10px;
  padding: 7px 10px;
}
.escpos-modal-formats {
  border-top: 1px solid #e9ecef;
  padding: 12px 16px 4px;
}
.escpos-modal-formats-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #6c757d;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.escpos-modal-format-item {
  display: flex;
  align-items: center;
  font-size: 0.88rem;
  padding: 8px 12px;
  border-left: none;
  border-right: none;
}
.escpos-modal-format-item--selected {
  background: #f0f2f5;
  font-weight: 600;
}
</style>
